import React from 'react';
import {
  Users, MapPin, Briefcase, Calendar,
  Banknote, UserPlus, Globe, TrendingUp, Newspaper, ExternalLink,
} from 'lucide-react';
import {
  EntityAvatar,
  InsightChip,
  LeadCardShell,
  MetaDot,
} from './LeadCardShell';

// Per-insight icon mapping. Add new insights to keep the chips specific.
const INSIGHT_ICONS = {
  'Recently funded':            Banknote,
  'Actively hiring':            UserPlus,
  'Expanding internationally':  Globe,
  'Profitable':                 TrendingUp,
  'Strong news momentum':       Newspaper,
};

export default function CompanyCard({ company, logoUrl }) {
  const imageUrl = logoUrl || company.logoUrl || null;

  return (
    <LeadCardShell>
      {/* Header: logo + name + industry subtitle */}
      <div className="flex items-start gap-3.5">
        <EntityAvatar
          name={company.name}
          initials={company.initials}
          colorClass={company.avatarColor}
          imageUrl={imageUrl}
          shape="square"
          alt={`${company.name} logo`}
        />
        <div className="min-w-0 flex-1 pt-0.5">
          <div className="font-semibold text-[15px] tracking-tight text-gray-900 dark:text-white truncate">
            {company.name}
          </div>
          <div className="text-[13px] text-gray-500 dark:text-gray-400 truncate mt-0.5 flex items-center gap-1">
            <Briefcase className="w-3 h-3 text-gray-400 flex-shrink-0" strokeWidth={1.75} />
            <span className="truncate">{company.industry}</span>
          </div>
        </div>
      </div>

      <div className="mt-3 flex items-center gap-1.5 text-[12px] text-gray-500 dark:text-gray-400 overflow-hidden flex-wrap">
        {company.country && (
          <span className="inline-flex items-center gap-1">
            <MapPin className="w-3 h-3 flex-shrink-0 text-gray-400" strokeWidth={1.75} />
            <span className="text-gray-700 dark:text-gray-200 font-medium">{company.country}</span>
          </span>
        )}
        {company.employeeRange && (
          <>
            <MetaDot />
            <span className="inline-flex items-center gap-1">
              <Users className="w-3 h-3 flex-shrink-0 text-gray-400" strokeWidth={1.75} />
              <span>{company.employeeRange}</span>
            </span>
          </>
        )}
        {company.founded && (
          <>
            <MetaDot />
            <span className="inline-flex items-center gap-1">
              <Calendar className="w-3 h-3 flex-shrink-0 text-gray-400" strokeWidth={1.75} />
              <span>Founded {company.founded}</span>
            </span>
          </>
        )}
      </div>

      {company.websiteUrl && (
        <div className="mt-2 min-w-0">
          <a
            href={company.websiteUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="group/website inline-flex max-w-full items-center gap-1.5 text-[12px] text-gray-500 transition-colors hover:text-brand-700 dark:text-gray-400 dark:hover:text-brand-300"
          >
            <Globe className="h-3 w-3 flex-shrink-0 text-gray-400 transition-colors group-hover/website:text-brand-500 dark:text-gray-500 dark:group-hover/website:text-brand-300" strokeWidth={1.75} />
            <span className="truncate font-medium text-gray-600 group-hover/website:text-brand-700 dark:text-gray-300 dark:group-hover/website:text-brand-300">
              {company.websiteLabel || company.websiteUrl}
            </span>
            <ExternalLink className="h-3 w-3 flex-shrink-0 text-gray-300 transition-colors group-hover/website:text-brand-500 dark:text-gray-600 dark:group-hover/website:text-brand-300" strokeWidth={1.75} />
          </a>
        </div>
      )}

      {company.insights && company.insights.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-1.5">
          {company.insights.map((i, index) => (
            <InsightChip key={`${i}-${index}`} label={i} iconMap={INSIGHT_ICONS} />
          ))}
        </div>
      )}
    </LeadCardShell>
  );
}
